import { Spacer, Text } from "@hackthenorth/north";
import {
  SquaresPlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Button } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { IMAGE_FILE_TYPES, Modal } from "src/shared/components";
import { HackerAPIFile } from "src/shared/utils/hackerapi";
import { EditIcon, EyeIcon } from "src/static/icons";
import { EmptyImage } from "src/static/img";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import Dropzone from "src/views/goose-games/components/dropzone";
import { StoreItem } from "src/views/goose-games/types";
import { useFileDropzone } from "src/views/hacker/rsvp/career/useFileDropzone";

import FooterActions from "../../../../footerActions";
import { MEDAL_PROGRESS_MAX } from "../../../hacker/home/quest/constants";
import PrizeView from "../../../hacker/store/prize/view";
import { MODAL_FIELDS } from "../../quest/constants";
import DeleteModal from "../../quest/delete";
export interface EditModalProps {
  item: StoreItem;
  isOpen: boolean;
  onDelete: (id: number, successMessage: string, errorMessage: string) => void;
  onConfirm: (
    id: number,
    successMessage: string,
    errorMessage: string,
    name?: string,
    pointCost?: number,
    quantityAvailable?: number,
    userRedeptionLimit?: number,
    description?: string,
    imageUrl?: string
  ) => void;
  onCancel: () => void;
}
const EditModal: React.FC<EditModalProps> = ({
  item,
  isOpen,
  onDelete,
  onConfirm,
  onCancel,
}) => {
  const field = MODAL_FIELDS["PRIZE"].edit;
  const [name, setName] = useState(item.name);
  const [points, setPoints] = useState<string | undefined>(
    String(item.point_cost)
  );
  const [isAvailable, setIsAvailable] = useState<boolean>(!item.is_sold_out);
  const [limit, setLimit] = useState<number>(item.user_redemption_limit);
  const [inventoryAmount, setInventoryAmount] = useState<string | undefined>(
    String(item.quantity_available)
  );
  const [description, setDescription] = useState<string | undefined>(
    item.description
  );
  const [imageFile, setImageFile] = useState<HackerAPIFile[] | undefined>(
    undefined
  );
  const [imageURI, setImageURI] = useState<string | undefined>(item.image_url);

  const ACCEPTED_FILE_TYPES = IMAGE_FILE_TYPES;

  const { dropzoneInputRef, addFile, onFileError, setDropzoneErrorMessage } =
    useFileDropzone(
      imageFile?.[0] ?? undefined,
      (files) => {
        setImageFile(files);
        if (files && files.length > 0) setImageURI(files[0].uri);
      },
      ACCEPTED_FILE_TYPES
    );

  const baseFormValues = {
    name,
    isAvailable,
    limit,
    inventoryAmount,
    description,
    points,
    imageURI,
  };

  const [showPreview, setShowPreview] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const hasNonEmptyRequiredFields =
    baseFormValues.name &&
    baseFormValues.isAvailable !== undefined &&
    baseFormValues.limit > 0 &&
    baseFormValues.inventoryAmount &&
    baseFormValues.points;

  const hasChangedValues =
    baseFormValues.name !== item.name ||
    baseFormValues.description !== item.description ||
    baseFormValues.limit !== item.user_redemption_limit ||
    Number(baseFormValues.points) !== item.point_cost ||
    baseFormValues.isAvailable !== !item.is_sold_out ||
    Number(baseFormValues.inventoryAmount) !== item.quantity_available ||
    baseFormValues.imageURI !== item.image_url;

  const handleConfirmation = () => {
    if (hasChangedValues && hasNonEmptyRequiredFields) {
      onConfirm(
        item.id,
        field.success,
        field.error,
        name,
        points ? Number(points) : 0,
        inventoryAmount ? Number(inventoryAmount) : 0,
        limit,
        description,
        imageFile && imageFile.length > 0 ? imageFile[0].uri : undefined
      );
    }
  };

  const handleAvailableChange = (input: string) => {
    if (input === "AVAILABLE") {
      setInventoryAmount("1");
      setIsAvailable(true);
    } else if (input === "SOLD_OUT") {
      setInventoryAmount("0");
      setIsAvailable(false);
    }
  };

  useEffect(() => {
    if (inventoryAmount && Number(inventoryAmount) > 0 && !isAvailable) {
      setIsAvailable(true);
    }
  }, [inventoryAmount, isAvailable]);

  return (
    <Modal isOpen={isOpen}>
      {showPreview ? (
        <>
          {showDeleteConfirmation && (
            <DeleteModal
              isOpen={showDeleteConfirmation}
              onConfirm={() =>
                onDelete(
                  item.id,
                  MODAL_FIELDS["PRIZE"].delete.success,
                  MODAL_FIELDS["PRIZE"].delete.error
                )
              }
              onCancel={() => setShowDeleteConfirmation(false)}
              type="PRIZE"
            />
          )}
          <ModalWrapper>
            <PreviewHeaderContainer>
              <Text mods="h3 bold">Prize preview</Text>
              <ActionIconsContainer>
                <Button
                  color="tertiary"
                  leadingIcon={<EditIcon width={18} height={18} />}
                  size="sm"
                  onClick={() => setShowPreview(false)}
                />
                <Button
                  color="tertiary"
                  leadingIcon={<TrashIcon width={20} height={20} />}
                  size="sm"
                  onClick={() => setShowDeleteConfirmation(true)}
                />
                <Button
                  color="tertiary"
                  leadingIcon={<XMarkIcon width={20} height={20} />}
                  size="sm"
                  onClick={() => onCancel()}
                />
              </ActionIconsContainer>
            </PreviewHeaderContainer>
            <PrizeView item={item} currentPoints={MEDAL_PROGRESS_MAX} />
          </ModalWrapper>
        </>
      ) : (
        <>
          <HeaderContainer>
            <IconContainer>
              <HeaderIcon>
                <SquaresPlusIcon />
              </HeaderIcon>
              <Text mods="h3 bold">{field.title}</Text>
            </IconContainer>
            <Button
              color="tertiary"
              leadingIcon={<EyeIcon width={18} height={18} />}
              size="md"
              onClick={() => setShowPreview(true)}
            >
              Preview
            </Button>
          </HeaderContainer>
          <FormContainer>
            <FirstLayer>
              <Text mods="body bold">Prize Photo</Text>
              <Dropzone
                acceptedFileTypes={IMAGE_FILE_TYPES}
                onFilesUpload={addFile}
                onError={onFileError}
                ref={dropzoneInputRef}
                onBlur={() => setDropzoneErrorMessage("")}
                max={1}
              >
                <ItemImage
                  src={imageURI ? imageURI : EmptyImage}
                  alt={`${item.name} Image`}
                  isAvailable={isAvailable}
                />
              </Dropzone>
              <DescriptionWrapper>
                <NameWrapper>
                  <Text mods="body bold">{field.name}</Text>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={field.name_placeholder}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                    }}
                  />
                </NameWrapper>
                <DropdownWrapper>
                  <Text mods="body bold">{field.type}</Text>
                  <select
                    value={isAvailable ? "AVAILABLE" : "SOLD_OUT"}
                    onChange={(e) => handleAvailableChange(e.target.value)}
                    onBlur={(e) => handleAvailableChange(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                    }}
                  >
                    {(
                      field.type_options as Array<{
                        label: string;
                        value: string;
                      }>
                    ).map((opt, i) => (
                      <option key={i} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </DropdownWrapper>
              </DescriptionWrapper>
            </FirstLayer>

            <SecondLayer>
              <DescriptionWrapper>
                <Text mods="body bold">Description</Text>
                <DescriptionTextarea
                  placeholder="Give a short 1-2 sentence description..."
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </DescriptionWrapper>
            </SecondLayer>

            <ThirdLayer>
              <CounterWrapper>
                <Text mods="body bold">Maximum redeemable times</Text>
                <CounterContainer>
                  <CounterButton
                    onClick={() =>
                      setLimit((prev) => (prev > 1 ? prev - 1 : prev))
                    }
                  >
                    −
                  </CounterButton>
                  <CounterValue>{limit}</CounterValue>
                  <CounterButton onClick={() => setLimit((prev) => prev + 1)}>
                    +
                  </CounterButton>
                </CounterContainer>
              </CounterWrapper>
              <NumberWrapper>
                <Text mods="body bold">{field.count_input}</Text>
                <input
                  type="number"
                  value={inventoryAmount}
                  onChange={(e) => setInventoryAmount(e.target.value)}
                  placeholder={field.count_input_placeholder}
                  style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
                />
              </NumberWrapper>
              <NumberWrapper>
                <Text mods="body bold">{field.num_input}</Text>
                <input
                  type="number"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  placeholder={field.num_input_placeholder}
                  style={{ width: "100%", padding: "8px", borderRadius: "4px" }}
                />
              </NumberWrapper>
            </ThirdLayer>
          </FormContainer>
          <Spacer height={36} />
          <FooterActions
            confirmText={"Save Changes"}
            onConfirm={handleConfirmation}
            onCancel={onCancel}
            isConfirmDisabled={!hasNonEmptyRequiredFields || !hasChangedValues}
          />
        </>
      )}
    </Modal>
  );
};

export default EditModal;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;
  justify-content: space-between;
`;

const PreviewHeaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: 24px;
  padding-inline: 36px;
  width: 100%;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundGray};
  border-bottom: ${({ theme }) =>
    `1px solid ${theme.globalConstants.color.borderGray}`};
`;

const ModalWrapper = styled.div`
  padding-top: 64px;
`;

const ActionIconsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderIcon = styled.div<{ backgroundColor?: string }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
`;

const ItemImage = styled.img<{ isAvailable: boolean }>`
  aspect-ratio: 1;
  border-radius: 8px;
  width: 170px;
  height: 170px;
  object-fit: cover;

  opacity: ${({ isAvailable }) => (isAvailable ? 1 : 0.5)};

  ${mediaQueries.large} {
    width: 140px;
    height: 140px;
  }

  ${mediaQueries.medium} {
    width: 170px;
    height: 170px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FirstLayer = styled.div`
  display: flex;
  gap: 24px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const SecondLayer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }
`;

const ThirdLayer = styled.div`
  display: flex;
  gap: 20px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const CounterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const CounterContainer = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  overflow: hidden;
`;

const CounterButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};

  &:hover {
    background-color: ${({ theme }) =>
      theme.globalConstants.color.backgroundGray};
  }
`;

const CounterValue = styled.div`
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  border-right: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderGray};
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundLight};
`;

const NumberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;
